import type { AxisOption, AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { SortByOption } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type {
    AxisGroupByControlsMeta,
    AxisSortByOptionMeta,
} from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type {
    KpiAxisConfigurationMetaDefaults,
    KpiAxisControlsMeta,
} from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import {
    type KpiBinLabelAffixes,
    toKpiBinLabelAffixesFromUnitRep,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_UNIFORM_BIN_COUNT } from 'constants/ppa';

import type { PpaMetaControlNode, PpaMetaOptionItem, PricePackArchitectureMetaResponse } from './packArchitectureMeta.types';

const SORT_BY_API_IDS = new Set<string>(['sort_on_alphabet', 'sort_on_value_sales', 'sort_on_volume_sales']);

const toSortByOption = (id?: string | null): SortByOption => {
    if (id && SORT_BY_API_IDS.has(id)) {
        return id as SortByOption;
    }
    return 'sort_on_alphabet';
};

export const resolveGroupByTemplateLabel = (label: string, dimensionLabel: string) =>
    label.replace(/\{\$?[#/]?[^}]*group_by_options[^}]*\}/gi, dimensionLabel);

const toAxisOption = (item: PpaMetaOptionItem): AxisOption => ({
    label: item.label ?? item.id ?? '',
    value: item.id ?? '',
});

export const findMetaOptionGroup = (meta: PricePackArchitectureMetaResponse | undefined, id: string) =>
    meta?.options?.find((option) => option.id === id);

export const findAxisConfig = (meta: PricePackArchitectureMetaResponse | undefined, axisId: string) => {
    const axisGroup = findMetaOptionGroup(meta, 'axis');
    return axisGroup?.options?.find((option) => option.id === axisId);
};

export const getXAxisGroupBySection = (xAxis?: PpaMetaControlNode) =>
    xAxis?.options?.find((section) => section.id === 'group_by_options');

export const getXAxisGroupByOptions = (xAxis?: PpaMetaControlNode) =>
    getXAxisGroupBySection(xAxis)?.group_by_options ?? [];

export const buildAxisGroupByControlsMeta = (
    xAxis?: PpaMetaControlNode,
    dimensionLabel = '',
): AxisGroupByControlsMeta | null => {
    const controls = getXAxisGroupBySection(xAxis)?.options;
    if (!controls?.length) return null;

    const sortByNode = controls.find((control) => control.id === 'sort_by');
    const showTopNode = controls.find((control) => control.id === 'show_top_n');
    const ownDimensionNode = controls.find((control) => control.id === 'own_dimension_sort');

    const sortOptions: AxisSortByOptionMeta[] = (sortByNode?.options ?? []).map((option) => ({
        id: toSortByOption(option.id),
        label: option.label ?? '',
        defaultSelected: option.default_value === true,
        orderOptions: (option.options ?? []).map((orderOption) => ({
            id: orderOption.id === 'desc' ? 'desc' : 'asc',
            label: orderOption.label ?? '',
            defaultSelected: orderOption.default_value === true,
        })),
    }));

    const showTopSlider = showTopNode?.options?.[0];
    const consolidateCheckbox = findNestedControl(showTopNode?.options ?? null, 'consolidate_as_others');
    const ownDimensionCheckbox = ownDimensionNode?.options?.[0];

    return {
        sortBy: {
            label: sortByNode?.label ?? 'Sort By',
            options: sortOptions,
        },
        showTop: {
            label: showTopNode?.label ?? showTopSlider?.label ?? 'Show Top',
            step: showTopSlider?.step ?? 1,
            consolidateAsOthers: {
                label: resolveGroupByTemplateLabel(consolidateCheckbox?.label ?? '', dimensionLabel),
                defaultSelected: consolidateCheckbox?.default_value === true,
            },
        },
        ownDimensionSort: ownDimensionCheckbox
            ? {
                  sectionLabel: ownDimensionNode?.label ?? 'Preview',
                  label: resolveGroupByTemplateLabel(ownDimensionCheckbox.label ?? '', dimensionLabel),
                  defaultSelected: ownDimensionCheckbox.default_value === true,
              }
            : null,
    };
};

export const getAxisConfigurationDefaultsFromMeta = (
    controlsMeta: AxisGroupByControlsMeta | null | undefined,
) => {
    if (!controlsMeta) return undefined;

    const defaultSortOption =
        controlsMeta.sortBy.options.find((option) => option.defaultSelected) ??
        controlsMeta.sortBy.options[0];
    const defaultOrder =
        defaultSortOption?.orderOptions.find((option) => option.defaultSelected)?.id ??
        defaultSortOption?.orderOptions[0]?.id ??
        'asc';

    return {
        sortBy: defaultSortOption?.id,
        sortOrder: defaultOrder,
        groupRestAsOther: controlsMeta.showTop.consolidateAsOthers.defaultSelected,
        showOwnVendorFirst: controlsMeta.ownDimensionSort?.defaultSelected ?? false,
    };
};

export const getXAxisColumnOptionsSection = (xAxis?: PpaMetaControlNode) =>
    xAxis?.options?.find((item) => item.id === 'column_options');

export const getXAxisColumnOptions = (xAxis?: PpaMetaControlNode) => {
    const section = getXAxisColumnOptionsSection(xAxis);
    return section?.options?.[0]?.column_options ?? section?.column_options ?? [];
};

const normalizeKpiId = (kpiId: string) => kpiId.toLowerCase().replace(/^ps_/, '');


const inferKpiBinLabelAffixes = (kpiId: string, currencyPrefix: string): KpiBinLabelAffixes => {
    const normalized = normalizeKpiId(kpiId);

    if (normalized.includes('price') ) {
        return { prefix: currencyPrefix };
    }
    if (normalized.includes('ounce')) {
        return { suffix: 'oz' };
    }
    if (normalized === 'count' ) {
        return { suffix: 'ct' };
    }

    return {};
};

/** Resolves PA-01 unit representation for KPI axis bin preview labels. */
export const getKpiBinLabelAffixesFromColumnOption = (
    option?: PpaMetaOptionItem | null,
    currencyPrefix = '',
): KpiBinLabelAffixes => {
    if (!option) return {};

    const prefix = option.valuePrefix ?? option.value_prefix;
    const suffix = option.valueSuffix ?? option.value_suffix;
    if (prefix || suffix) {
        return { prefix, suffix };
    }

    const unitRep = option.unit_rep?.trim();
    if (unitRep) {
        return toKpiBinLabelAffixesFromUnitRep(unitRep, currencyPrefix);
    }

    return option.id ? inferKpiBinLabelAffixes(option.id, currencyPrefix) : {};
};

export const getKpiBinLabelAffixesForXAxis = (
    xAxisId: string,
    xAxisConfig?: PpaMetaControlNode,
    currencyPrefix = '',
): KpiBinLabelAffixes => {
    if (!xAxisId) return {};

    const option = getXAxisColumnOptions(xAxisConfig).find((item) => item.id === xAxisId);
    return getKpiBinLabelAffixesFromColumnOption(option, currencyPrefix);
};

const toNumericDefault = (value: boolean | number | null | undefined, fallback: number) =>
    typeof value === 'number' ? value : fallback;

const findNestedControl = (
    nodes: PpaMetaControlNode[] | null | undefined,
    id: string,
): PpaMetaControlNode | undefined => {
    if (!nodes?.length) return undefined;

    for (const node of nodes) {
        if (node.id === id) return node;
        const nested = findNestedControl(node.options ?? null, id);
        if (nested) return nested;
    }

    return undefined;
};

const getColumnOptionControlRoots = (xAxis?: PpaMetaControlNode) => {
    const section = getXAxisColumnOptionsSection(xAxis);
    return section?.options ?? [];
};

const toKpiInputMeta = (
    node: PpaMetaControlNode | undefined,
    labelFallback: string,
    valueFallback: number,
) => ({
    label: node?.label ?? labelFallback,
    defaultValue: toNumericDefault(node?.default_value, valueFallback),
});

const buildKpiRangeControlsMetaFromNode = (
    rangeNode?: PpaMetaControlNode,
): KpiAxisControlsMeta['range'] => {
    const rangeSlider = rangeNode?.options?.[0];
    const rangeMinInput = rangeSlider?.options?.find((option) => option.id === 'min');
    const rangeMaxInput = rangeSlider?.options?.find((option) => option.id === 'max');

    return {
        label: rangeNode?.label ?? 'Range',
        step: rangeSlider?.step ?? 1,
        minInput: toKpiInputMeta(rangeMinInput, 'Min.', 0),
        maxInput: toKpiInputMeta(rangeMaxInput, 'Max.', 100),
    };
};

const buildKpiBinsControlsMetaFromNode = (
    binsNode?: PpaMetaControlNode,
): KpiAxisControlsMeta['bins'] => {
    const uniformToggle = findNestedControl(binsNode?.options ?? null, 'uniform');
    const customToggle = findNestedControl(binsNode?.options ?? null, 'custom');
    const uniformCountInput = findNestedControl(uniformToggle?.options ?? null, 'count');
    const uniformSizeInput = findNestedControl(uniformToggle?.options ?? null, 'size');
    const customBreakPointInput = findNestedControl(binsNode?.options ?? null, 'break_point');

    return {
        label: binsNode?.label ?? 'Bins',
        uniform: {
            label: uniformToggle?.label ?? 'Uniform',
            defaultSelected: uniformToggle?.default_value !== false,
            count: toKpiInputMeta(uniformCountInput, 'Count', DEFAULT_UNIFORM_BIN_COUNT),
            size: toKpiInputMeta(uniformSizeInput, 'Size', 1),
        },
        custom: {
            label: customToggle?.label ?? 'Custom',
            defaultSelected: Boolean(customToggle?.default_value),
            breakPoint: toKpiInputMeta(customBreakPointInput, 'Break Point', 0),
        },
    };
};

export const buildKpiAxisControlsMeta = (xAxis?: PpaMetaControlNode): KpiAxisControlsMeta | null => {
    const controlRoots = getColumnOptionControlRoots(xAxis);
    if (!controlRoots.length) return null;

    const rangeNode = findNestedControl(controlRoots, 'range');
    const binsNode = findNestedControl(controlRoots, 'bins');
    if (!rangeNode && !binsNode) return null;

    return {
        range: buildKpiRangeControlsMetaFromNode(rangeNode),
        bins: buildKpiBinsControlsMetaFromNode(binsNode),
    };
};

export const getKpiAxisConfigurationDefaultsFromMeta = (
    controlsMeta: KpiAxisControlsMeta | null | undefined,
): KpiAxisConfigurationMetaDefaults | undefined => {
    if (!controlsMeta) return undefined;

    const { uniform } = controlsMeta.bins;
    const binningMethod = uniform.defaultSelected ? 'uniform' : 'custom';
    const countDefault = uniform.count.defaultValue;
    const binCount = countDefault > 0 ? countDefault : DEFAULT_UNIFORM_BIN_COUNT;

    return { binningMethod, binCount };
};

export const buildColumnOptionsGroups = (
    items: PpaMetaOptionItem[],
    fallbackTitle = '',
): AxisOptionGroup[] => {
    if (!items.length) return [];

    const hasDimensionLabels = items.some((item) => item.dimension_label);

    if (!hasDimensionLabels) {
        return [{ title: '', options: items.map(toAxisOption) }];
    }

    const groupsByLabel = new Map<string, PpaMetaOptionItem[]>();
    for (const item of items) {
        const groupTitle = item.dimension_label ?? fallbackTitle;
        const existing = groupsByLabel.get(groupTitle) ?? [];
        existing.push(item);
        groupsByLabel.set(groupTitle, existing);
    }

    return Array.from(groupsByLabel.entries()).map(([title, groupItems]) => ({
        title,
        options: groupItems.map(toAxisOption),
    }));
};

export const mapAttributeOptionsToGroups = (items: PpaMetaOptionItem[]): AxisOptionGroup[] =>
    buildColumnOptionsGroups(items);

export const getXAxisKpiOptionIds = (xAxisConfig?: PpaMetaControlNode): string[] =>
    getXAxisColumnOptions(xAxisConfig)
        .map((item) => item.id)
        .filter((id): id is string => Boolean(id));

export const buildXAxisOptionGroups = (xAxis?: PpaMetaControlNode): AxisOptionGroup[] => {
    if (!xAxis) return [];

    return [
        ...buildColumnOptionsGroups(getXAxisGroupByOptions(xAxis)),
        ...buildColumnOptionsGroups(getXAxisColumnOptions(xAxis)),
    ];
};

export const buildYAxisOptionGroups = (yAxis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(yAxis?.column_options ?? []);

export const buildBubbleRepresentsOptionGroups = (axis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(axis?.group_by_options ?? []);

export const buildBubbleSizeOptionGroups = (axis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(axis?.column_options ?? []);

export const getDefaultOptionId = (items: PpaMetaOptionItem[]) =>
    items.find((item) => item.is_default)?.id ?? items[0]?.id ?? '';

export type SalesKpiIds = {
    valueSales: string;
    volumeSales: string;
};

/** BRD PA-07 axis preview/sort aggregates use pos_data totals. */
export const AXIS_PREVIEW_SALES_KPI_IDS: SalesKpiIds = {
    valueSales: 'total_value',
    volumeSales: 'total_eq_volume',
};

/** Table metric checkboxes from meta `ppa_grid` → `table_kpi` (PA-02). */
export const getTableKpiOptionItems = (meta?: PricePackArchitectureMetaResponse): PpaMetaControlNode[] => {
    const gridNode = findMetaOptionGroup(meta, 'ppa_grid');
    const tableKpiNode = gridNode?.options?.find((option) => option.id === 'table_kpi');
    return tableKpiNode?.options ?? [];
};

/** Table info tooltip from meta `ppa_grid` node. */
export const getTableTooltip = (meta?: PricePackArchitectureMetaResponse): string => {
    const gridNode = findMetaOptionGroup(meta, 'ppa_grid');
    return gridNode?.tooltip?.trim() ?? '';
};

export const getHeatMapToggleConfig = (meta: PricePackArchitectureMetaResponse | undefined) => {
    const toggleGroup =
        findMetaOptionGroup(meta, 'heat_map_toggle') ?? findMetaOptionGroup(meta, 'toggle');
    return toggleGroup?.options?.find((option) => option.id === 'enable_heat_map');
};

export const getAxisLabel = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    for (const group of optionGroups) {
        const match = group.options.find((option) => option.value === axisValue);
        if (match) return match.label;
    }
    if (!axisValue) return '';
    return axisValue
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const getAxisGroupTitle = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    for (const group of optionGroups) {
        if (group.options.some((option) => option.value === axisValue)) {
            return group.title?.trim() ?? '';
        }
    }
    return '';
};

export type ChartTitleSegment = { text: string; emphasis?: boolean };

export type PackArchitectureChartTitleInput = {
    xAxis: string;
    xAxisConfig?: PpaMetaControlNode;
    xAxisOptionGroups: AxisOptionGroup[];
    yAxis: string;
    yAxisOptionGroups: AxisOptionGroup[];
    bubbleRepresents: string;
    bubbleRepresentsOptionGroups: AxisOptionGroup[];
};

export const buildPackArchitectureChartTitleSegments = ({
    xAxis,
    xAxisConfig,
    xAxisOptionGroups,
    yAxis,
    yAxisOptionGroups,
    bubbleRepresents,
    bubbleRepresentsOptionGroups,
}: PackArchitectureChartTitleInput): ChartTitleSegment[] => {
    const xLabel = getAxisLabel(xAxis, xAxisOptionGroups);
    const yLabel = getAxisLabel(yAxis, yAxisOptionGroups);
    const bubbleLabel = getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups);
    const yGroup = getAxisGroupTitle(yAxis, yAxisOptionGroups) || yLabel.split(/\s+/)[0] || '';
    const xGroup = getAxisGroupTitle(xAxis, xAxisOptionGroups).trim();
    const isKpiXAxis = getXAxisColumnOptions(xAxisConfig).some((item) => item.id === xAxis);
    const segments: ChartTitleSegment[] = [];
    const bold = (text: string) => segments.push({ text, emphasis: true });
    const plain = (text: string) => segments.push({ text });

    if (isKpiXAxis) {
        if (yGroup) bold(yGroup);
        if (xGroup && xGroup.toLowerCase() !== yGroup.toLowerCase()) {
            if (segments.length) plain(' ');
            bold(xGroup);
        }
        plain(' Architecture');
    } else {
        if (yGroup) bold(yGroup);
        plain(' Architecture');
        if (xLabel) {
            plain(' of ');
            bold(xLabel);
        }
    }

    if (bubbleLabel) {
        plain(' by ');
        bold(bubbleLabel);
    }

    return segments;
};

export const buildPackArchitectureChartTitle = (input: PackArchitectureChartTitleInput) =>
    buildPackArchitectureChartTitleSegments(input)
        .map((segment) => segment.text)
        .join('');

export const buildPackArchitectureTableTitle = (xAxisLabel: string, bubbleRepresentsLabel: string) => {
    if (xAxisLabel && bubbleRepresentsLabel) {
        return `${xAxisLabel} by ${bubbleRepresentsLabel}`;
    }
    if (xAxisLabel) return xAxisLabel;
    return 'Data Table';
};

export {
    buildPpaChartRequest,
    isKpiXAxisValue,
} from './packArchitectureChartRequest.helpers';
